import React, { useState } from 'react';
import { Code2, Copy, Check, Layers, Sparkles, Globe } from 'lucide-react';
import CodeBlock from '../components/CodeBlock';
import { 
  AddressForm,
  NationalAddressField 
} from 'salla-address-manager/components/react';

// Mock data for demo purposes
const mockCountries = [
  { id: '1473353380', name: 'Saudi Arabia', code: 'SA' },
  { id: '1473353411', name: 'United Arab Emirates', code: 'AE' },
  { id: '1473353455', name: 'Kuwait', code: 'KW' },
  { id: '1473353477', name: 'Oman', code: 'OM' },
];

const mockCities: Record<string, Array<{ id: string; name: string }>> = {
  '1473353380': [ // Saudi Arabia
    { id: 'riyadh', name: 'Riyadh' },
    { id: 'jeddah', name: 'Jeddah' },
    { id: 'dammam', name: 'Dammam' },
    { id: 'makkah', name: 'Makkah' },
    { id: 'madina', name: 'Madinah' },
  ],
  '1473353411': [ // UAE
    { id: 'dubai', name: 'Dubai' },
    { id: 'abu-dhabi', name: 'Abu Dhabi' },
    { id: 'sharjah', name: 'Sharjah' },
  ],
  '1473353455': [ // Kuwait
    { id: 'kuwait-city', name: 'Kuwait City' },
    { id: 'al-ahmadi', name: 'Al Ahmadi' },
  ],
};

const mockDistricts: Record<string, Array<{ id: string; name: string }>> = {
  'riyadh': [
    { id: 'olaya', name: 'Olaya' },
    { id: 'malaz', name: 'Malaz' },
    { id: 'najd', name: 'Najd' },
    { id: 'qasr-al-hokm', name: 'Qasr Al Hokm' },
  ],
  'jeddah': [
    { id: 'al-balad', name: 'Al Balad' },
    { id: 'al-hamra', name: 'Al Hamra' },
    { id: 'corniche', name: 'Corniche' },
  ],
};

// Mock national address data
const mockNationalAddresses = [
  { value: '12345678', label: '1234 5678 - Olaya, Riyadh', data: { 
    city: 'riyadh', 
    district: 'olaya', 
    street: 'King Fahd Road',
    building_number: '1234',
    unit_number: '5678'
  }},
  { value: '87654321', label: '8765 4321 - Al Balad, Jeddah', data: {
    city: 'jeddah',
    district: 'al-balad',
    street: 'Corniche Road',
    building_number: '8765',
    unit_number: '4321'
  }},
];

const ComponentsPage: React.FC = () => {
  const [copiedCode, setCopiedCode] = useState<string | null>(null);
  const [selectedFramework, setSelectedFramework] = useState<'react' | 'vue3' | 'vue2'>('react');

  const copyToClipboard = (code: string, id: string) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(id);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  // Mock API functions
  const mockCountryOptions = async () => mockCountries;
  const mockCityOptions = async (countryId: string | number) => mockCities[String(countryId)] || [];
  const mockDistrictOptions = async (cityId: string | number) => mockDistricts[String(cityId)] || [];
  const mockNationalAddressQuery = async ({ address }: { address: string }) => {
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 300));
    // Filter mock addresses based on search
    return {
      data: {
        data: mockNationalAddresses.filter(addr => 
          addr.value.includes(address) || addr.label.toLowerCase().includes(address.toLowerCase())
        )
      }
    };
  };

  // React Examples
  const basicExampleCode = `import { AddressForm } from 'salla-address-manager/components/react';

function MyAddressForm() {
  const countryOptions = async () => [
    { id: '1473353380', name: 'Saudi Arabia' },
    { id: '1473353411', name: 'United Arab Emirates' },
  ];
  
  const cityOptions = async (countryId) => {
    const cities = {
      '1473353380': [
        { id: 'riyadh', name: 'Riyadh' },
        { id: 'jeddah', name: 'Jeddah' },
      ],
    };
    return cities[countryId] || [];
  };

  return (
    <AddressForm.Root 
      config={{ 
        locale: 'en',
        countryOptions,
        cityOptions,
      }}
      onSubmit={(data) => console.log('Submitted:', data)}
    >
      <AddressForm.Country 
        label="Country"
        placeholder="Select your country"
        wrapperClassName="flex flex-col space-y-1"
      />
      <AddressForm.City 
        label="City"
        placeholder="Select your city"
        wrapperClassName="flex flex-col space-y-1"
      />
      <AddressForm.District 
        label="District"
        placeholder="Select or enter district"
        wrapperClassName="flex flex-col space-y-1"
      />
      <AddressForm.Street 
        label="Street Address"
        placeholder="Enter street address"
        wrapperClassName="flex flex-col space-y-1"
      />
      <AddressForm.Description 
        label="Additional Details"
        placeholder="Building number, apartment, etc."
        wrapperClassName="flex flex-col space-y-1"
      />
      <AddressForm.Submit />
    </AddressForm.Root>
  );
}`;

  const nationalAddressExampleCode = `import { AddressForm, NationalAddressField } from 'salla-address-manager/components/react';

function NationalAddressForm() {
  const handleNationalAddress = async ({ address }) => {
    const res = await fetch(\`/api/national-address?q=\${address}\`);
    return res.json();
  };

  return (
    <AddressForm.Root 
      config={{ 
        locale: 'en',
        enableNationalAddress: true,
        countryOptions: async () => [
          { id: '1473353380', name: 'Saudi Arabia' },
        ],
        cityOptions: async (countryId) => [
          { id: 'riyadh', name: 'Riyadh' },
          { id: 'jeddah', name: 'Jeddah' },
        ],
      }}
      onSubmit={(data) => console.log('Submitted:', data)}
    >
      <AddressForm.Country 
        label="Country"
        placeholder="Select country"
        wrapperClassName="flex flex-col space-y-1"
      />
      
      <NationalAddressField
        queryFn={handleNationalAddress}
        transform={(res) => res.data?.data || []}
        label="National Address"
        placeholder="e.g., 12345678"
        wrapperClassName="flex flex-col space-y-1"
        className="border border-gray-300 rounded-md px-3 py-2"
      />
      
      <AddressForm.City 
        label="City"
        placeholder="Auto-filled from national address"
        wrapperClassName="flex flex-col space-y-1"
      />
      <AddressForm.District 
        label="District"
        placeholder="Auto-filled from national address"
        wrapperClassName="flex flex-col space-y-1"
      />
      <AddressForm.Street 
        label="Street"
        placeholder="Auto-filled from national address"
        wrapperClassName="flex flex-col space-y-1"
      />
      <AddressForm.Submit />
    </AddressForm.Root>
  );
}`;

  // Vue 3 Example
  const vue3ExampleCode = `<template>
  <AddressForm 
    :config="formConfig"
    @submit="handleSubmit"
    @error="handleError"
    className="space-y-4"
  >
    <AddressField 
      name="country"
      label="Country"
      placeholder="Select your country"
      wrapper-class-name="flex flex-col space-y-1"
    />
    <AddressField 
      name="city"
      label="City"
      placeholder="Select your city"
      wrapper-class-name="flex flex-col space-y-1"
    />
    <AddressField 
      name="district"
      label="District"
      placeholder="Select or enter district"
      wrapper-class-name="flex flex-col space-y-1"
    />
    <AddressField 
      name="street"
      label="Street Address"
      placeholder="Enter street address"
      wrapper-class-name="flex flex-col space-y-1"
    />
    <AddressSubmitButton />
  </AddressForm>
</template>

<script setup>
import { AddressForm, AddressField, AddressSubmitButton } from 'salla-address-manager/components/vue3';

const formConfig = {
  locale: 'en',
  countryOptions: async () => [
    { id: '1473353380', name: 'Saudi Arabia' },
    { id: '1473353411', name: 'United Arab Emirates' },
  ],
  cityOptions: async (countryId) => {
    const cities = {
      '1473353380': [
        { id: 'riyadh', name: 'Riyadh' },
        { id: 'jeddah', name: 'Jeddah' },
      ],
    };
    return cities[countryId] || [];
  },
};

const handleSubmit = (data) => {
  console.log('Form submitted:', data);
  alert('Address saved successfully!');
};

const handleError = (errors) => {
  console.error('Validation errors:', errors);
};
</script>`;

  // Vue 2 Example
  const vue2ExampleCode = `<template>
  <AddressForm 
    :config="formConfig"
    @submit="handleSubmit"
    @error="handleError"
    class="space-y-4"
  >
    <AddressField 
      name="country"
      label="Country"
      placeholder="Select your country"
      wrapper-class-name="flex flex-col space-y-1"
    />
    <AddressField 
      name="city"
      label="City"
      placeholder="Select your city"
      wrapper-class-name="flex flex-col space-y-1"
    />
    <AddressField 
      name="street"
      label="Street Address"
      placeholder="Enter street address"
      wrapper-class-name="flex flex-col space-y-1"
    />
    <AddressSubmitButton />
  </AddressForm>
</template>

<script>
import { AddressForm, AddressField, AddressSubmitButton } from 'salla-address-manager/components/vue2';

export default {
  components: {
    AddressForm,
    AddressField,
    AddressSubmitButton
  },
  data() {
    return {
      formConfig: {
        locale: 'en',
        countryOptions: async () => [
          { id: '1473353380', name: 'Saudi Arabia' },
          { id: '1473353411', name: 'United Arab Emirates' },
        ],
        cityOptions: async (countryId) => {
          const cities = {
            '1473353380': [
              { id: 'riyadh', name: 'Riyadh' },
              { id: 'jeddah', name: 'Jeddah' },
            ],
          };
          return cities[countryId] || [];
        },
      }
    };
  },
  methods: {
    handleSubmit(data) {
      console.log('Form submitted:', data);
      alert('Address saved successfully!');
    },
    handleError(errors) {
      console.error('Validation errors:', errors);
    }
  }
};
</script>`;

  return (
    <div className="container-custom py-12">
      {/* Header */}
      <div className="max-w-4xl mx-auto mb-12">
        <div className="inline-flex items-center space-x-2 bg-gradient-to-r from-blue-500/10 to-purple-500/10 px-4 py-2 rounded-full text-sm font-medium text-blue-600 mb-6">
          <Layers className="w-4 h-4" />
          <span>UI Components Library</span>
        </div>
        
        <h1 className="text-5xl font-bold text-gray-900 mb-4">
          Address Form Components
        </h1>
        
        <p className="text-xl text-gray-600 leading-relaxed">
          Pre-built, production-ready components for React, Vue 3, and Vue 2 that work out of the box.
          Fully customizable with proper labels, placeholders, and built-in validation.
        </p>
      </div>

      {/* Framework Selector */}
      <div className="max-w-4xl mx-auto mb-8">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center space-x-4">
            <Globe className="w-5 h-5 text-gray-500" />
            <span className="font-medium text-gray-700">Framework:</span>
            <div className="flex space-x-2">
              {(['react', 'vue3', 'vue2'] as const).map((fw) => (
                <button
                  key={fw}
                  onClick={() => setSelectedFramework(fw)}
                  className={`px-4 py-2 rounded-md font-medium transition-colors ${
                    selectedFramework === fw
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {fw === 'react' ? 'React' : fw === 'vue3' ? 'Vue 3' : 'Vue 2'}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Quick Navigation */}
      <div className="max-w-4xl mx-auto mb-12">
        <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-6 border border-blue-100">
          <div className="flex items-start space-x-4">
            <Sparkles className="w-6 h-6 text-blue-600 mt-1 flex-shrink-0" />
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Why Use Components?</h3>
              <ul className="space-y-1 text-gray-600 text-sm">
                <li>✅ Ready-to-use UI components with proper labels and placeholders</li>
                <li>✅ Built on the same headless API - switch between headless and components anytime</li>
                <li>✅ Fully customizable styling and behavior</li>
                <li>✅ TypeScript support with full type safety</li>
                <li>✅ Accessible by default with proper ARIA attributes</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Basic Example - React */}
      {selectedFramework === 'react' && (
        <section className="max-w-4xl mx-auto mb-16">
          <div className="bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden">
            <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-4 border-b border-gray-200">
              <h2 className="text-2xl font-bold text-gray-900">Basic Address Form with Labels & Placeholders</h2>
              <p className="text-gray-600 mt-1">Complete form with proper labels, placeholders, and mock data</p>
            </div>
            
            <div className="p-6">
              <div className="mb-6 bg-gray-50 rounded-lg p-6 border border-gray-200">
                <AddressForm.Root 
                  config={{ 
                    locale: 'en',
                    countryOptions: { queryFn: mockCountryOptions },
                    cityOptions: { queryFn: mockCityOptions },
                    districtOptions: { queryFn: mockDistrictOptions },
                  }}
                  onSubmit={(data) => {
                    console.log('Form submitted:', data);
                    alert(`Form submitted!\n\n${JSON.stringify(data, null, 2)}`);
                  }}
                  className="space-y-4"
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <AddressForm.Country 
                      label="Country"
                      placeholder="Select your country"
                      wrapperClassName="flex flex-col space-y-1"
                      className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <AddressForm.City 
                      label="City"
                      placeholder="Select your city"
                      wrapperClassName="flex flex-col space-y-1"
                      className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <AddressForm.District 
                    label="District"
                    placeholder="Select or enter district name"
                    wrapperClassName="flex flex-col space-y-1"
                    className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <AddressForm.Street 
                    label="Street Address"
                    placeholder="Enter street address"
                    wrapperClassName="flex flex-col space-y-1"
                    className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <AddressForm.Description 
                    label="Additional Details"
                    placeholder="Building number, apartment, floor, etc."
                    wrapperClassName="flex flex-col space-y-1"
                    className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <AddressForm.Submit 
                    className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-md font-medium transition-colors w-full md:w-auto"
                  />
                </AddressForm.Root>
              </div>

              <div className="relative">
                <button
                  onClick={() => copyToClipboard(basicExampleCode, 'basic')}
                  className="absolute top-2 right-2 p-2 text-gray-500 hover:text-gray-700 transition-colors z-10"
                  title="Copy code"
                >
                  {copiedCode === 'basic' ? (
                    <Check className="w-4 h-4 text-green-600" />
                  ) : (
                    <Copy className="w-4 h-4" />
                  )}
                </button>
                <CodeBlock code={basicExampleCode} language="tsx" />
              </div>
            </div>
          </div>
        </section>
      )}

      {/* National Address Example - React */}
      {selectedFramework === 'react' && (
        <section className="max-w-4xl mx-auto mb-16">
          <div className="bg-white rounded-lg shadow-lg border-2 border-green-200 overflow-hidden">
            <div className="bg-gradient-to-r from-green-50 to-blue-50 px-6 py-4 border-b border-green-200">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">🌟 National Address Integration</h2>
                  <p className="text-gray-600 mt-1">Saudi Arabia national address autocomplete with auto-fill</p>
                </div>
                <div className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm font-semibold">
                  Featured
                </div>
              </div>
            </div>
            
            <div className="p-6">
              <div className="mb-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  <strong>💡 How it works:</strong> Select "Saudi Arabia" as the country, then start typing a national address (e.g., "1234").
                  The component will show autocomplete suggestions and automatically fill city, district, and street fields when you select an address.
                </p>
              </div>

              <div className="mb-6 bg-gray-50 rounded-lg p-6 border border-gray-200">
                <AddressForm.Root 
                  config={{ 
                    locale: 'en',
                    enableNationalAddress: true,
                    countryOptions: { queryFn: mockCountryOptions },
                    cityOptions: { queryFn: mockCityOptions },
                    districtOptions: { queryFn: mockDistrictOptions },
                  }}
                  onSubmit={(data) => {
                    console.log('Form submitted:', data);
                    alert(`Form submitted!\n\n${JSON.stringify(data, null, 2)}`);
                  }}
                  className="space-y-4"
                >
                  <AddressForm.Country 
                    label="Country"
                    placeholder="Select country (try Saudi Arabia)"
                    wrapperClassName="flex flex-col space-y-1"
                    className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                  
                  <NationalAddressField
                    queryFn={mockNationalAddressQuery}
                    transform={(res) => res.data?.data || []}
                    label="National Address"
                    placeholder="Type national address (e.g., 12345678)"
                    wrapperClassName="flex flex-col space-y-1"
                    className="border-2 border-green-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                    dropdownClassName="border border-gray-200 rounded-md shadow-lg max-h-60 overflow-y-auto"
                    debounceMs={300}
                    minSearchLength={1}
                  />
                  
                  <AddressForm.City 
                    label="City"
                    placeholder="Auto-filled from national address"
                    wrapperClassName="flex flex-col space-y-1"
                    className="border border-gray-300 rounded-md px-3 py-2 bg-gray-50"
                    disabled={true}
                  />
                  <AddressForm.District 
                    label="District"
                    placeholder="Auto-filled from national address"
                    wrapperClassName="flex flex-col space-y-1"
                    className="border border-gray-300 rounded-md px-3 py-2 bg-gray-50"
                    disabled={true}
                  />
                  <AddressForm.Street 
                    label="Street"
                    placeholder="Auto-filled from national address"
                    wrapperClassName="flex flex-col space-y-1"
                    className="border border-gray-300 rounded-md px-3 py-2 bg-gray-50"
                    disabled={true}
                  />
                  <AddressForm.Submit 
                    className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-md font-medium transition-colors w-full"
                  />
                </AddressForm.Root>
              </div>

              <div className="relative">
                <button
                  onClick={() => copyToClipboard(nationalAddressExampleCode, 'national')}
                  className="absolute top-2 right-2 p-2 text-gray-500 hover:text-gray-700 transition-colors z-10"
                  title="Copy code"
                >
                  {copiedCode === 'national' ? (
                    <Check className="w-4 h-4 text-green-600" />
                  ) : (
                    <Copy className="w-4 h-4" />
                  )}
                </button>
                <CodeBlock code={nationalAddressExampleCode} language="tsx" />
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Vue 3 Example */}
      {selectedFramework === 'vue3' && (
        <section className="max-w-4xl mx-auto mb-16">
          <div className="bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden">
            <div className="bg-gradient-to-r from-green-50 to-blue-50 px-6 py-4 border-b border-green-100">
              <h2 className="text-2xl font-bold text-gray-900">Vue 3 Address Form</h2>
              <p className="text-gray-600 mt-1">Using Composition API with proper labels and placeholders</p>
            </div>
            
            <div className="p-6">
              <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-sm text-yellow-800">
                  <strong>Note:</strong> This is a code example. The live demo uses React components.
                  For Vue 3, import from <code className="bg-yellow-100 px-1 rounded">salla-address-manager/components/vue3</code>
                </p>
              </div>

              <div className="relative">
                <button
                  onClick={() => copyToClipboard(vue3ExampleCode, 'vue3')}
                  className="absolute top-2 right-2 p-2 text-gray-500 hover:text-gray-700 transition-colors z-10"
                  title="Copy code"
                >
                  {copiedCode === 'vue3' ? (
                    <Check className="w-4 h-4 text-green-600" />
                  ) : (
                    <Copy className="w-4 h-4" />
                  )}
                </button>
                <CodeBlock code={vue3ExampleCode} language="vue" />
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Vue 2 Example */}
      {selectedFramework === 'vue2' && (
        <section className="max-w-4xl mx-auto mb-16">
          <div className="bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden">
            <div className="bg-gradient-to-r from-orange-50 to-red-50 px-6 py-4 border-b border-orange-100">
              <h2 className="text-2xl font-bold text-gray-900">Vue 2 Address Form</h2>
              <p className="text-gray-600 mt-1">Using Options API with proper labels and placeholders</p>
            </div>
            
            <div className="p-6">
              <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-sm text-yellow-800">
                  <strong>Note:</strong> This is a code example. The live demo uses React components.
                  For Vue 2, import from <code className="bg-yellow-100 px-1 rounded">salla-address-manager/components/vue2</code>
                </p>
              </div>

              <div className="relative">
                <button
                  onClick={() => copyToClipboard(vue2ExampleCode, 'vue2')}
                  className="absolute top-2 right-2 p-2 text-gray-500 hover:text-gray-700 transition-colors z-10"
                  title="Copy code"
                >
                  {copiedCode === 'vue2' ? (
                    <Check className="w-4 h-4 text-green-600" />
                  ) : (
                    <Copy className="w-4 h-4" />
                  )}
                </button>
                <CodeBlock code={vue2ExampleCode} language="vue" />
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Component API Reference */}
      <section className="max-w-4xl mx-auto mb-16">
        <div className="bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden">
          <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-4 border-b border-gray-200">
            <h2 className="text-2xl font-bold text-gray-900 flex items-center space-x-2">
              <Code2 className="w-6 h-6" />
              <span>Component API Reference</span>
            </h2>
          </div>
          
          <div className="p-6 space-y-6">
            <div>
              <h3 className="font-semibold text-lg text-gray-900 mb-2">Field Props (React, Vue 3, Vue 2)</h3>
              <div className="bg-gray-50 rounded p-4 font-mono text-sm space-y-2">
                <div><span className="text-purple-600">label</span>?: string | ReactNode</div>
                <div><span className="text-purple-600">placeholder</span>?: string</div>
                <div><span className="text-purple-600">className</span>?: string</div>
                <div><span className="text-purple-600">wrapperClassName</span>?: string</div>
                <div><span className="text-purple-600">disabled</span>?: boolean</div>
                <div><span className="text-purple-600">required</span>?: boolean</div>
              </div>
            </div>

            <div>
              <h3 className="font-semibold text-lg text-gray-900 mb-2">NationalAddressField Props (React)</h3>
              <div className="bg-gray-50 rounded p-4 font-mono text-sm space-y-2">
                <div><span className="text-purple-600">queryFn</span>: (params) =&gt; Promise (required)</div>
                <div><span className="text-purple-600">transform</span>?: (response) =&gt; NationalAddressItem[]</div>
                <div><span className="text-purple-600">label</span>?: string</div>
                <div><span className="text-purple-600">placeholder</span>?: string</div>
                <div><span className="text-purple-600">debounceMs</span>?: number (default: 400)</div>
                <div><span className="text-purple-600">minSearchLength</span>?: number (default: 3)</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Next Steps */}
      <section className="max-w-4xl mx-auto">
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg p-8 text-white">
          <h2 className="text-2xl font-bold mb-4">Ready to Get Started?</h2>
          <p className="mb-6 text-blue-100">
            Check out the complete documentation and API reference to learn more about
            advanced features and customization options.
          </p>
          <div className="flex flex-wrap gap-4">
            <a
              href="/docs"
              className="bg-white text-blue-600 px-6 py-3 rounded-lg font-semibold hover:bg-blue-50 transition-colors"
            >
              View Documentation
            </a>
            <a
              href="/examples"
              className="bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-800 transition-colors"
            >
              See More Examples
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ComponentsPage;
